/**
 * Add Task Operation
 * Creates a new task with optional group, category, and node type
 */
import { generateTaskId } from '../utils';
import { calculateNextNodePosition } from '../../../../../utils/nodePositioning';

export const createAddTaskHandler = (localSprint, setLocalSprint, recordAction) => {
  return (groupIdOrStatus = null, category = null, nodeType = 'task', options = {}) => {
    const taskId = generateTaskId();
    const now = new Date().toISOString();
    
    // Check if first param is a status ID (for Kanban) or group ID (for Flow)
    const isStatusId = typeof groupIdOrStatus === 'string' && 
      ['pending', 'in_progress', 'completed', 'blocked'].includes(groupIdOrStatus) ||
      (localSprint?.statuses?.some(s => s.id === groupIdOrStatus));
    
    const groupId = isStatusId ? null : groupIdOrStatus;
    const status = isStatusId ? groupIdOrStatus : 'pending';
    
    // Support optional tags, initial name, initial icon, and explicit position
    const { tags, initialName, initialIcon, position: explicitPosition, sourceCampaign } = options;
    
    // Use explicit position if provided, otherwise calculate next position
    const position = explicitPosition || calculateNextNodePosition(localSprint?.tasks);
    
    const baseTask = {
      id: taskId,
      name: initialName || (nodeType === 'decision' ? '' : nodeType === 'milestone' ? 'Milestone' : nodeType === 'wait' ? 'Wait for e-mail' : ''),
      order: (localSprint?.tasks?.length || 0) + 1,
      groupId: groupId,
      dependsOn: [],
      status: status,
      x: position.x,
      y: position.y,
      createdAt: now,
      updatedAt: now,
      startedAt: null,
      completedAt: null,
      estimatedDurationMinutes: null,
      dueDate: null,
      ...(category && { category }),
      ...(tags && tags.length > 0 && { tags, category: tags[0] }),
      ...(initialIcon && { icon: initialIcon }),
      ...(sourceCampaign && { sourceCampaign }),
    };

    // Add node type specific properties
    if (nodeType === 'decision') {
      baseTask.nodeType = 'decision';
      baseTask.options = [
        { id: 'yes', label: 'Yes', color: '#22c55e' },
        { id: 'no', label: 'No', color: '#ef4444' },
      ];
      baseTask.answer = null;
    } else if (nodeType === 'milestone') {
      baseTask.nodeType = 'milestone';
      if (options.milestoneAnchor) {
        baseTask.milestoneAnchor = options.milestoneAnchor;
      }
    } else if (nodeType === 'wait') {
      baseTask.nodeType = 'wait';
      baseTask.waitFor = 'email';
      baseTask.waitDetails = '';
      baseTask.fulfilledAt = null;
    } else if (nodeType === 'note') {
      baseTask.nodeType = 'note';
      if (options.noteAnchor) {
        baseTask.noteAnchor = options.noteAnchor;
      }
    }

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return { ...prev, tasks: [...tasks, baseTask] };
    });

    // Record for undo
    if (recordAction) {
      const typeName = nodeType === 'decision' ? 'Decision' : nodeType === 'wait' ? 'Wait for' : nodeType === 'milestone' ? 'Milestone' : nodeType === 'note' ? 'Note' : 'Mission';
      recordAction({
        type: 'ADD_TASK',
        description: `Add ${typeName}: "${baseTask.name}"`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return { ...prev, tasks: prev.tasks.filter(t => t.id !== taskId) };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return { ...prev, tasks: [...prev.tasks, baseTask] };
          });
        },
      });
    }

    return taskId;
  };
};
